/**
 * Packages selector behaviour
 *
 * Manages active-bedroom + active-duration state inside a
 * `.packages-selector` section. All visual filtering is driven
 * by `[data-active-bedroom]` on the section root — this script
 * just sets that attribute in response to hover / focus / click
 * on the bedroom buttons (top nav AND the inline tabs duplicated
 * below the panel heading).
 *
 * The script also wires up:
 *   • Quantity +/- buttons inside the active product panel
 *   • Duration tab toggle (visual only — wire-up to shopping
 *     plans lands in a follow-up; for now selecting a duration
 *     just updates the "Duration Selected" row)
 *   • Cart form submit posts to /cart/add.js so the page doesn't
 *     full-reload; falls back to native form submit if fetch fails
 *
 * Section auto-init runs on DOMContentLoaded and on Shopify's
 * `shopify:section:load` event so it survives Theme Editor reloads.
 */

(function () {
  function init(section) {
    if (!section) return;
    /* Idempotent: re-running init shouldn't double-attach handlers
       because we use named handler functions stored on the section
       node. The init guard from before caused stale-state issues
       when Shopify section reloads kept the dataset set across
       partial re-renders. */
    if (section.__packagesInit) return;
    section.__packagesInit = true;

    var nav = section.querySelector('.packages-selector__nav');

    /**
     * Update the animated underline so it ends at the right edge
     * of the active TOP-LEVEL nav button (not the inline tabs).
     * The line is positioned `left: 0; right: var(--nav-line-right)`
     * relative to `.packages-selector__nav` — so right = the
     * distance from the nav's right edge to the active button's
     * right edge.
     */
    function updateNavLine() {
      if (!nav) return;
      var activeBtn = nav.querySelector(
        '.packages-selector__nav-item[aria-selected="true"]'
      );
      if (!activeBtn) return;

      var navRect = nav.getBoundingClientRect();
      var btnRect = activeBtn.getBoundingClientRect();
      var rightOffset = Math.max(0, navRect.right - btnRect.right);
      nav.style.setProperty('--nav-line-right', rightOffset + 'px');
    }

    function setActiveBedroom(index) {
      if (!index) return;
      section.setAttribute('data-active-bedroom', index);

      section.querySelectorAll('[data-bedroom-button]').forEach(function (btn) {
        btn.setAttribute(
          'aria-selected',
          btn.getAttribute('data-bedroom-button') === index ? 'true' : 'false'
        );
      });

      updateNavLine();
    }

    /* Top nav bedroom buttons — switch on hover, focus, AND click.
       Event delegation at the nav level: a single handler catches
       events on any descendant nav-item, even if the button DOM
       gets re-rendered (e.g. Shopify section reload). More robust
       than per-button addEventListener. */
    if (nav) {
      nav.addEventListener('pointerover', function (event) {
        var btn = event.target.closest('.packages-selector__nav-item[data-bedroom-button]');
        if (btn) setActiveBedroom(btn.getAttribute('data-bedroom-button'));
      });
      nav.addEventListener('focusin', function (event) {
        var btn = event.target.closest('.packages-selector__nav-item[data-bedroom-button]');
        if (btn) setActiveBedroom(btn.getAttribute('data-bedroom-button'));
      });
      nav.addEventListener('click', function (event) {
        var btn = event.target.closest('.packages-selector__nav-item[data-bedroom-button]');
        if (btn) {
          event.preventDefault();
          setActiveBedroom(btn.getAttribute('data-bedroom-button'));
        }
      });
    }

    /* Inline bedroom tabs inside the product-detail panel — CLICK ONLY.
       Also delegated at the section level. Hover-switching here
       would feel twitchy as the mouse moves through the panel —
       users commit with a click. */
    section.addEventListener('click', function (event) {
      var btn = event.target.closest('.packages-selector__inline-tab[data-bedroom-button]');
      if (btn) {
        event.preventDefault();
        setActiveBedroom(btn.getAttribute('data-bedroom-button'));
      }
    });

    /**
     * Format pence as a £ string. Mirrors Shopify's `money` filter
     * for GBP — keeps prices in cents on data attrs (the same unit
     * Shopify uses internally) and formats at render time only.
     */
    function formatGBP(cents) {
      if (cents == null || isNaN(cents)) return '—';
      var pounds = cents / 100;
      return '£' + pounds.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    /* Duration tabs — switching one updates:
        • aria-selected on the duration buttons in this panel
        • "Duration Selected" / "Due each month" / "Due at checkout" rows
        • the hidden selling_plan input in the add-to-cart form
       Price + months come straight off the active button's data
       attributes (set by Liquid using the linked product's
       selling_plan_allocations). No fetch needed. */
    section.querySelectorAll('[data-duration-button]').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();

        var panel = btn.closest('.packages-selector__panel');
        if (!panel) return;

        panel.querySelectorAll('[data-duration-button]').forEach(function (other) {
          other.setAttribute('aria-selected', other === btn ? 'true' : 'false');
        });

        var label = btn.getAttribute('data-duration-button') || '';
        var months = parseInt(btn.getAttribute('data-months'), 10) || 0;
        var monthlyCents = parseInt(btn.getAttribute('data-monthly-price'), 10) || 0;
        var sellingPlanId = btn.getAttribute('data-selling-plan-id') || '';

        var durationDisplay = panel.querySelector('[data-duration-display]');
        if (durationDisplay) durationDisplay.textContent = label;

        var monthlyDisplay = panel.querySelector('[data-monthly-display]');
        if (monthlyDisplay) {
          monthlyDisplay.textContent =
            monthlyCents > 0 ? formatGBP(monthlyCents) + ' per month' : '—';
        }

        var checkoutDisplay = panel.querySelector('[data-checkout-display]');
        if (checkoutDisplay) {
          /* "Due at checkout" = first month only. Shopify charges
             one instalment at checkout, not the full rental duration.
             Previously this was `monthlyCents * months` which displayed
             the lifetime rental cost as the at-checkout amount. */
          checkoutDisplay.textContent = monthlyCents > 0 ? formatGBP(monthlyCents) : '—';
        }

        /* Sync the hidden form input so add-to-cart submits the
           selected duration's selling plan. */
        var planInput = panel.querySelector('[data-selling-plan-input]');
        if (planInput) planInput.value = sellingPlanId;
      });
    });

    /* Default each panel to its LONGEST duration on load: click the tab with the
       highest data-months so the handler above updates the display rows AND the
       hidden selling-plan input (so add-to-cart uses the longest term too).
       Fail-safe — only clicks a real, rendered tab; no-op if none/already set. */
    section.querySelectorAll('.packages-selector__panel').forEach(function (panel) {
      var longest = null, most = -1;
      panel.querySelectorAll('[data-duration-button]').forEach(function (b) {
        var m = parseInt(b.getAttribute('data-months'), 10) || 0;
        if (m > most) { most = m; longest = b; }
      });
      if (longest && longest.getAttribute('aria-selected') !== 'true') longest.click();
    });

    /* Quantity +/- */
    section.querySelectorAll('.packages-selector__qty').forEach(function (group) {
      var input = group.querySelector('.packages-selector__qty-input');
      var dec = group.querySelector('[data-qty-decrement]');
      var inc = group.querySelector('[data-qty-increment]');
      if (!input) return;

      function clamp(n) {
        var min = parseInt(input.min, 10) || 1;
        return Math.max(min, n);
      }

      if (dec) {
        dec.addEventListener('click', function () {
          input.value = clamp((parseInt(input.value, 10) || 1) - 1);
        });
      }
      if (inc) {
        inc.addEventListener('click', function () {
          input.value = clamp((parseInt(input.value, 10) || 1) + 1);
        });
      }
    });

    /* Add-to-cart — AJAX with full cart-drawer refresh.
       Mirrors the theme's standard `product-form.js` pattern:
        1. Build FormData from the form
        2. Append `sections` (list of section IDs the cart drawer cares
           about) + `sections_url` (current path) so Shopify's
           /cart/add.js response includes the re-rendered HTML for
           those sections
        3. Pass that response to the live <cart-drawer> element's
           `renderContents()` so the drawer's items / count / total
           all update in-place without a page reload
       Previously this only dispatched `cart:refresh` / `cart:updated`
       custom events — but the theme's cart drawer doesn't listen for
       those (it expects to be handed pre-rendered section HTML), so
       the user had to refresh the page to see the new line item. */
    section.querySelectorAll('[data-packages-add-form]').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();

        var btn = form.querySelector('.packages-selector__add-btn');
        var originalLabel = btn ? btn.textContent : '';
        var cartDrawer = document.querySelector('cart-drawer');

        if (btn) {
          btn.disabled = true;
          btn.textContent = 'Adding...';
        }

        var formData = new FormData(form);
        /* Ask Shopify to re-render the cart drawer's sections in the
           same response — saves a second roundtrip and keeps the
           drawer DOM perfectly in sync with the cart state. */
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          var sectionIds = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        fetch(window.routes && window.routes.cart_add_url
          ? window.routes.cart_add_url + '.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        })
          .then(function (res) {
            if (!res.ok) throw new Error('cart_add_failed');
            return res.json();
          })
          .then(function (response) {
            /* Hand the response to the cart drawer so it replaces its
               own section HTML with the freshly-rendered one. This is
               what makes the new line item appear without a refresh. */
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(response);
            }
            /* Keep the legacy custom events for any other listeners
               (analytics, third-party apps) that may be wired up. */
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            document.dispatchEvent(new CustomEvent('cart:updated'));
            if (btn) btn.textContent = 'Added';
            setTimeout(function () {
              if (btn) {
                btn.textContent = originalLabel;
                btn.disabled = false;
              }
            }, 1500);
          })
          .catch(function () {
            if (btn) {
              btn.textContent = originalLabel;
              btn.disabled = false;
            }
            // Last-resort: fall through to native submit
            form.removeEventListener('submit', arguments.callee);
            form.submit();
          });
      });
    });

    /* Position the nav-line on initial render + whenever the
       viewport changes shape. Images inside the nav buttons load
       lazily — so also recompute once the section has loaded any
       remaining assets to catch the case where the button width
       grows after the image decodes. */
    function scheduleLineUpdate() {
      requestAnimationFrame(updateNavLine);
    }
    scheduleLineUpdate();
    window.addEventListener('resize', scheduleLineUpdate);
    window.addEventListener('load', scheduleLineUpdate);

    /* Image-decode safety net: a few buttons may decode after the
       initial load fires, which would change the active button's
       right edge. Listen for each nav image's load event. */
    section
      .querySelectorAll('.packages-selector__nav-image')
      .forEach(function (img) {
        if (img.complete) return;
        img.addEventListener('load', scheduleLineUpdate, { once: true });
      });
  }

  function initAll() {
    document.querySelectorAll('.packages-selector').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.packages-selector');
    if (section) init(section);
  });
})();
